import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAccess } from '@/lib/accessLogs';
import { DndContext, DragOverlay, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import LabNavigation from '@/components/feature/LabNavigation';
import DraggableAnalyseRow from './components/DraggableAnalyseRow';
import DroppableCategoryArea from './components/DroppableCategoryArea';

interface AnalyseCatalogue {
  id: string;
  code: string;
  nom: string;
  unite: string;
  categorie: string;
  description: string | null;
  val_min_0_1_an: number | null;
  val_max_0_1_an: number | null;
  val_min_1_5_ans: number | null;
  val_max_1_5_ans: number | null;
  val_min_5_12_ans: number | null;
  val_max_5_12_ans: number | null;
  val_min_12_18_ans: number | null;
  val_max_12_18_ans: number | null;
  val_min_adulte_homme: number | null;
  val_max_adulte_homme: number | null;
  val_min_adulte_femme: number | null;
  val_max_adulte_femme: number | null;
  val_min_60_plus: number | null;
  val_max_60_plus: number | null;
}

interface RangeEdition {
  min: string;
  max: string;
}

interface AnalyseEdition {
  [key: string]: RangeEdition;
}

const RANGE_KEYS: { key: string; label: string; icon: string }[] = [
  { key: '0_1_an', label: '0-1 an', icon: 'ri-user-smile-line' },
  { key: '1_5_ans', label: '1-5 ans', icon: 'ri-user-3-line' },
  { key: '5_12_ans', label: '5-12 ans', icon: 'ri-user-line' },
  { key: '12_18_ans', label: '12-18 ans', icon: 'ri-team-line' },
  { key: 'adulte_homme', label: 'Adulte H', icon: 'ri-men-line' },
  { key: 'adulte_femme', label: 'Adulte F', icon: 'ri-women-line' },
  { key: '60_plus', label: '60+ ans', icon: 'ri-user-heart-line' },
];

const DB_FIELDS: Record<string, { min: string; max: string }> = {
  '0_1_an': { min: 'val_min_0_1_an', max: 'val_max_0_1_an' },
  '1_5_ans': { min: 'val_min_1_5_ans', max: 'val_max_1_5_ans' },
  '5_12_ans': { min: 'val_min_5_12_ans', max: 'val_max_5_12_ans' },
  '12_18_ans': { min: 'val_min_12_18_ans', max: 'val_max_12_18_ans' },
  'adulte_homme': { min: 'val_min_adulte_homme', max: 'val_max_adulte_homme' },
  'adulte_femme': { min: 'val_min_adulte_femme', max: 'val_max_adulte_femme' },
  '60_plus': { min: 'val_min_60_plus', max: 'val_max_60_plus' },
};

export default function ValeursReferencePage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [catalogue, setCatalogue] = useState<AnalyseCatalogue[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedAnalyses, setExpandedAnalyses] = useState<Set<string>>(new Set());
  const [editions, setEditions] = useState<Record<string, AnalyseEdition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMessages, setSaveMessages] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});
  const [dirtyAnalyses, setDirtyAnalyses] = useState<Set<string>>(new Set());

  // États pour l'édition du nom et de la catégorie
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState('');

  // État pour la création d'une nouvelle analyse
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAnalyse, setNewAnalyse] = useState({
    code: '',
    nom: '',
    unite: '',
    categorie: '',
    description: '',
    ranges: {
      '0_1_an': { min: '', max: '' },
      '1_5_ans': { min: '', max: '' },
      '5_12_ans': { min: '', max: '' },
      '12_18_ans': { min: '', max: '' },
      'adulte_homme': { min: '', max: '' },
      'adulte_femme': { min: '', max: '' },
      '60_plus': { min: '', max: '' },
    },
  });
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // État pour le drag & drop
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [movingAnalyse, setMovingAnalyse] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'laborantin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadCatalogue();
  }, [user, authLoading, role]);

  const loadCatalogue = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: loadErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('*')
        .order('categorie', { ascending: true })
        .order('nom', { ascending: true });

      if (loadErr) throw loadErr;
      setCatalogue((data || []) as AnalyseCatalogue[]);

      // Log accès en lecture
      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'VIEW',
          resourceType: 'ANALYSE_CATALOGUE',
          details: {
            operation: 'Consultation du catalogue des valeurs de référence',
            nombre_analyses: (data || []).length,
          },
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du catalogue');
    } finally {
      setLoading(false);
    }
  };

  const getCategorieIcon = (cat: string) => {
    const lowerCat = cat.toLowerCase();
    if (lowerCat.includes('rénal') || lowerCat.includes('renal')) return 'ri-drop-line';
    if (lowerCat.includes('hépat') || lowerCat.includes('hepat')) return 'ri-mental-health-line';
    if (lowerCat.includes('lipid') || lowerCat.includes('cholest')) return 'ri-heart-pulse-line';
    if (lowerCat.includes('enzyme')) return 'ri-test-tube-line';
    if (lowerCat.includes('hormone') || lowerCat.includes('thyro')) return 'ri-psychotherapy-line';
    if (lowerCat.includes('ionogramme') || lowerCat.includes('électro')) return 'ri-flashlight-line';
    if (lowerCat.includes('inflammatoire') || lowerCat.includes('inflammation')) return 'ri-virus-line';
    if (lowerCat.includes('protéine') || lowerCat.includes('proteine')) return 'ri-dna-line';
    if (lowerCat.includes('pancréat') || lowerCat.includes('pancreat')) return 'ri-restaurant-line';
    if (lowerCat.includes('hémostase') || lowerCat.includes('hemostase') || lowerCat.includes('coagulation')) return 'ri-contrast-drop-2-line';
    if (lowerCat.includes('hématologie') || lowerCat.includes('hematologie')) return 'ri-drop-fill';
    if (lowerCat.includes('sang') || lowerCat.includes('glyc')) return 'ri-contrast-drop-line';
    if (lowerCat.includes('minéral') || lowerCat.includes('mineral') || lowerCat.includes('martial')) return 'ri-capsule-line';
    if (lowerCat.includes('tumoral') || lowerCat.includes('marqueur')) return 'ri-microscope-line';
    if (lowerCat.includes('sérologie') || lowerCat.includes('serologie')) return 'ri-shield-line';
    if (lowerCat.includes('bactériologie') || lowerCat.includes('bacteriologie')) return 'ri-bug-line';
    if (lowerCat.includes('combiné') || lowerCat.includes('bilan')) return 'ri-file-list-3-line';
    if (lowerCat.includes('musculaire')) return 'ri-run-line';
    return 'ri-flask-line';
  };

  const initEditionForAnalyse = useCallback((analyse: AnalyseCatalogue): AnalyseEdition => {
    const ed: AnalyseEdition = {};
    for (const rk of RANGE_KEYS) {
      const fields = DB_FIELDS[rk.key];
      const minVal = (analyse as any)[fields.min];
      const maxVal = (analyse as any)[fields.max];
      ed[rk.key] = {
        min: minVal !== null && minVal !== undefined ? String(minVal) : '',
        max: maxVal !== null && maxVal !== undefined ? String(maxVal) : '',
      };
    }
    return ed;
  }, []);

  const handleExpandAnalyse = (analyseId: string) => {
    setExpandedAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(analyseId)) {
        next.delete(analyseId);
      } else {
        next.add(analyseId);
        // Initialiser l'édition si pas encore fait
        if (!editions[analyseId]) {
          const analyse = catalogue.find(a => a.id === analyseId);
          if (analyse) {
            setEditions(prevEd => ({ ...prevEd, [analyseId]: initEditionForAnalyse(analyse) }));
          }
        }
      }
      return next;
    });
  };

  const handleRangeChange = (analyseId: string, rangeKey: string, field: 'min' | 'max', value: string) => {
    // Accepter seulement nombres, point décimal et signe moins
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;

    setEditions(prev => {
      const current = prev[analyseId] || {};
      const currentRange = current[rangeKey] || { min: '', max: '' };
      return {
        ...prev,
        [analyseId]: {
          ...current,
          [rangeKey]: { ...currentRange, [field]: value },
        },
      };
    });

    // Marquer comme modifié
    setDirtyAnalyses(prev => new Set(prev).add(analyseId));
  };

  const isAnalyseDirty = (analyseId: string): boolean => {
    if (!dirtyAnalyses.has(analyseId)) return false;
    const analyse = catalogue.find(a => a.id === analyseId);
    if (!analyse) return false;
    const ed = editions[analyseId];
    if (!ed) return false;

    for (const rk of RANGE_KEYS) {
      const fields = DB_FIELDS[rk.key];
      const origMin = (analyse as any)[fields.min];
      const origMax = (analyse as any)[fields.max];
      const origMinStr = origMin !== null && origMin !== undefined ? String(origMin) : '';
      const origMaxStr = origMax !== null && origMax !== undefined ? String(origMax) : '';
      const currentRange = ed[rk.key];
      if (!currentRange) continue;
      if (currentRange.min !== origMinStr || currentRange.max !== origMaxStr) return true;
    }
    return false;
  };

  const handleSaveAnalyse = async (analyseId: string) => {
    const analyse = catalogue.find(a => a.id === analyseId);
    const edition = editions[analyseId];
    if (!analyse || !edition) return;

    setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'success', text: '' } }));

    try {
      const updateData: Record<string, number | null> = {};
      const changes: string[] = [];
      const oldValues: Record<string, { min: number | null; max: number | null }> = {};
      const newValues: Record<string, { min: number | null; max: number | null }> = {};

      for (const rk of RANGE_KEYS) {
        const fields = DB_FIELDS[rk.key];
        const range = edition[rk.key];
        if (!range) continue;

        const origMin = (analyse as any)[fields.min];
        const origMax = (analyse as any)[fields.max];
        const newMin = range.min.trim() === '' ? null : parseFloat(range.min);
        const newMax = range.max.trim() === '' ? null : parseFloat(range.max);

        if (newMin !== origMin) {
          updateData[fields.min] = newMin;
          changes.push(`${rk.label} min: ${origMin ?? '—'} → ${newMin ?? '—'}`);
        }
        if (newMax !== origMax) {
          updateData[fields.max] = newMax;
          changes.push(`${rk.label} max: ${origMax ?? '—'} → ${newMax ?? '—'}`);
        }

        oldValues[rk.key] = { min: origMin, max: origMax };
        newValues[rk.key] = { min: newMin, max: newMax };
      }

      if (Object.keys(updateData).length === 0) {
        setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'error', text: 'Aucune modification détectée' } }));
        return;
      }

      const { error: updateErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .update(updateData)
        .eq('id', analyseId);

      if (updateErr) throw updateErr;

      // Log la modification
      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'UPDATE',
          resourceType: 'ANALYSE_CATALOGUE',
          resourceId: analyseId,
          details: {
            operation: 'Modification des valeurs de référence',
            analyse_code: analyse.code,
            analyse_nom: analyse.nom,
            modifications: changes,
            anciennes_valeurs: oldValues,
            nouvelles_valeurs: newValues,
          },
        });
      }

      // Mettre à jour le catalogue local
      setCatalogue(prev =>
        prev.map(a => a.id === analyseId ? { ...a, ...updateData } : a)
      );

      // Nettoyer l'état dirty
      setDirtyAnalyses(prev => {
        const next = new Set(prev);
        next.delete(analyseId);
        return next;
      });

      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'success', text: `${changes.length} modification(s) enregistrée(s)` } }));

      // Effacer le message après 3s
      setTimeout(() => {
        setSaveMessages(prev => {
          const next = { ...prev };
          delete next[analyseId];
          return next;
        });
      }, 3000);
    } catch (err: any) {
      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'error', text: err.message || 'Erreur lors de l\'enregistrement' } }));
    }
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  // Sauvegarder le nouveau nom d'une analyse
  const handleSaveName = async (analyseId: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === catalogue.find(a => a.id === analyseId)?.nom) {
      setEditingNameId(null);
      return;
    }

    try {
      const { error: updateErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .update({ nom: trimmed })
        .eq('id', analyseId);

      if (updateErr) throw updateErr;

      setCatalogue(prev => prev.map(a => a.id === analyseId ? { ...a, nom: trimmed } : a));

      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'UPDATE',
          resourceType: 'ANALYSE_CATALOGUE',
          resourceId: analyseId,
          details: { operation: 'Renommage de l\'analyse', ancien_nom: catalogue.find(a => a.id === analyseId)?.nom, nouveau_nom: trimmed },
        });
      }

      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'success', text: 'Nom mis à jour' } }));
      setTimeout(() => setSaveMessages(prev => { const n = { ...prev }; delete n[analyseId]; return n; }), 2500);
    } catch (err: any) {
      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'error', text: err.message || 'Erreur' } }));
    }
    setEditingNameId(null);
  };

  // Sauvegarder la nouvelle catégorie d'une analyse
  const handleSaveCategory = async (analyseId: string, newCategory: string) => {
    const trimmed = newCategory.trim();
    if (!trimmed || trimmed === catalogue.find(a => a.id === analyseId)?.categorie) {
      setEditingCategoryId(null);
      return;
    }

    try {
      const { error: updateErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .update({ categorie: trimmed })
        .eq('id', analyseId);

      if (updateErr) throw updateErr;

      setCatalogue(prev => prev.map(a => a.id === analyseId ? { ...a, categorie: trimmed } : a));

      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'UPDATE',
          resourceType: 'ANALYSE_CATALOGUE',
          resourceId: analyseId,
          details: { operation: 'Changement de catégorie', ancienne_categorie: catalogue.find(a => a.id === analyseId)?.categorie, nouvelle_categorie: trimmed },
        });
      }

      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'success', text: 'Catégorie mise à jour' } }));
      setTimeout(() => setSaveMessages(prev => { const n = { ...prev }; delete n[analyseId]; return n; }), 2500);
    } catch (err: any) {
      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'error', text: err.message || 'Erreur' } }));
    }
    setEditingCategoryId(null);
  };

  // Ouvrir le modal de création
  const handleOpenCreateModal = () => {
    setCreateError('');
    setCreateSuccess('');
    setNewAnalyse({
      code: '',
      nom: '',
      unite: '',
      categorie: '',
      description: '',
      ranges: {
        '0_1_an': { min: '', max: '' },
        '1_5_ans': { min: '', max: '' },
        '5_12_ans': { min: '', max: '' },
        '12_18_ans': { min: '', max: '' },
        'adulte_homme': { min: '', max: '' },
        'adulte_femme': { min: '', max: '' },
        '60_plus': { min: '', max: '' },
      },
    });
    setShowCreateModal(true);
  };

  // Créer une nouvelle analyse
  const handleCreateAnalyse = async () => {
    setCreateError('');
    setCreateSuccess('');

    const trimmedNom = newAnalyse.nom.trim();
    const trimmedCode = newAnalyse.code.trim();
    const trimmedCategorie = newAnalyse.categorie.trim();

    if (!trimmedNom) {
      setCreateError('Le nom de l\'analyse est obligatoire');
      return;
    }
    if (!trimmedCategorie) {
      setCreateError('La catégorie est obligatoire');
      return;
    }

    setCreating(true);
    try {
      const insertData: Record<string, any> = {
        nom: trimmedNom,
        code: trimmedCode || trimmedNom.substring(0, 8).toUpperCase().replace(/\s/g, '_'),
        unite: newAnalyse.unite.trim() || '',
        categorie: trimmedCategorie,
        description: newAnalyse.description.trim() || null,
      };

      // Ajouter les plages de référence
      for (const rk of RANGE_KEYS) {
        const fields = DB_FIELDS[rk.key];
        const range = newAnalyse.ranges[rk.key];
        insertData[fields.min] = range.min.trim() !== '' ? parseFloat(range.min) : null;
        insertData[fields.max] = range.max.trim() !== '' ? parseFloat(range.max) : null;
      }

      const { data: created, error: insertErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .insert(insertData)
        .select()
        .single();

      if (insertErr) throw insertErr;

      // Log
      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'CREATE',
          resourceType: 'ANALYSE_CATALOGUE',
          resourceId: created.id,
          details: {
            operation: 'Création d\'une nouvelle analyse',
            analyse_code: insertData.code,
            analyse_nom: insertData.nom,
            categorie: insertData.categorie,
          },
        });
      }

      setCreateSuccess(`Analyse "${trimmedNom}" créée avec succès !`);
      setCatalogue(prev => [...prev, created as AnalyseCatalogue]);

      // Fermer le modal après 1.5s
      setTimeout(() => {
        setShowCreateModal(false);
        setCreateSuccess('');
      }, 1500);
    } catch (err: any) {
      setCreateError(err.message || 'Erreur lors de la création de l\'analyse');
    } finally {
      setCreating(false);
    }
  };

  // Handlers drag & drop
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const analyseId = active.id as string;
    // L'ID du droppable est au format "droppable-{nom catégorie}"
    const overId = over.id as string;
    if (!overId.startsWith('droppable-')) return;

    const targetCategory = overId.replace('droppable-', '');
    const analyse = catalogue.find(a => a.id === analyseId);
    if (!analyse) return;
    if (analyse.categorie === targetCategory) return;

    setMovingAnalyse(true);
    try {
      const { error: updateErr } = await supabase
        .from('analyses_biochimie_catalogue')
        .update({ categorie: targetCategory })
        .eq('id', analyseId);

      if (updateErr) throw updateErr;

      // Log
      if (user) {
        logAccess({
          userId: user.id,
          userRole: role || 'laborantin',
          actionType: 'UPDATE',
          resourceType: 'ANALYSE_CATALOGUE',
          resourceId: analyseId,
          details: {
            operation: 'Déplacement par glisser-déposer',
            analyse_code: analyse.code,
            analyse_nom: analyse.nom,
            ancienne_categorie: analyse.categorie,
            nouvelle_categorie: targetCategory,
          },
        });
      }

      setCatalogue(prev => prev.map(a => a.id === analyseId ? { ...a, categorie: targetCategory } : a));
      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'success', text: `Déplacé vers "${targetCategory}"` } }));
      setTimeout(() => setSaveMessages(prev => { const n = { ...prev }; delete n[analyseId]; return n; }), 3000);
    } catch (err: any) {
      setSaveMessages(prev => ({ ...prev, [analyseId]: { type: 'error', text: err.message || 'Erreur lors du déplacement' } }));
    } finally {
      setMovingAnalyse(false);
    }
  };

  // Trouver l'analyse active pour le DragOverlay
  const activeAnalyse = activeDragId ? catalogue.find(a => a.id === activeDragId) : null;

  // Liste unique des catégories existantes
  const toutesLesCategories = Array.from(new Set(catalogue.map(a => a.categorie))).sort();

  // Grouper par catégorie
  const groupedByCategory = catalogue.reduce((acc, a) => {
    const cat = a.categorie || 'Autres';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {} as Record<string, AnalyseCatalogue[]>);

  const categories = Object.keys(groupedByCategory).sort();

  // Filtrer
  const filterAnalyses = (analyses: AnalyseCatalogue[]) => {
    if (!searchTerm.trim()) return analyses;
    const term = searchTerm.toLowerCase();
    return analyses.filter(a =>
      a.nom.toLowerCase().includes(term) ||
      a.code.toLowerCase().includes(term) ||
      (a.description || '').toLowerCase().includes(term)
    );
  };

  const totalAnalyses = catalogue.length;
  const modifiedCount = Array.from(dirtyAnalyses).filter(id => isAnalyseDirty(id)).length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4 block"></i>
          <span className="text-gray-600">Chargement du catalogue...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* En-tête */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center">
                  <i className="ri-database-2-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">Valeurs de référence</h1>
                  <p className="text-gray-500 text-sm mt-0.5">
                    Gérez les valeurs normales de référence pour chaque analyse du catalogue ({totalAnalyses} analyses)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleOpenCreateModal}
                  className="px-5 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2 shadow-sm"
                >
                  <i className="ri-add-line text-lg"></i>
                  Nouvelle analyse
                </button>
                <button
                  onClick={() => navigate('/laborantin/dashboard')}
                  className="px-5 py-3 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-100 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-arrow-left-line"></i>
                  Retour
                </button>
              </div>
            </div>
            <LabNavigation />
          </div>

          {/* Message d'erreur global */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
              <p className="text-red-700 text-sm flex-1">{error}</p>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* Barre de recherche */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative flex-1 w-full">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-circle-line text-lg"></i>
                  </button>
                )}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une analyse par nom, code ou description..."
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                />
              </div>
              {modifiedCount > 0 && (
                <span className="text-sm text-amber-600 font-medium whitespace-nowrap flex items-center gap-1">
                  <i className="ri-edit-line"></i>
                  {modifiedCount} analyse{modifiedCount > 1 ? 's' : ''} modifiée{modifiedCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-teal-800 mb-1">Traçabilité complète</p>
                <p className="text-sm text-teal-700">
                  Chaque consultation de cette page et chaque modification des valeurs de référence sont automatiquement enregistrées dans les journaux d'accès (date, utilisateur, anciennes et nouvelles valeurs, IP).
                </p>
              </div>
            </div>
          </div>

          {/* Catalogue par catégories */}
          <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="space-y-3">
            {categories.map(cat => {
              const filtered = filterAnalyses(groupedByCategory[cat]);
              const isExpanded = expandedCategories.has(cat) || searchTerm.trim() !== '';
              const catIcon = getCategorieIcon(cat);
              const droppableId = `droppable-${cat}`;

              return (
                <div key={cat} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Entête catégorie */}
                  <button
                    onClick={() => toggleCategory(cat)}
                    className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-gray-50/50 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${catIcon} text-teal-600 text-lg`}></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-gray-800">{cat}</h2>
                        <p className="text-sm text-gray-500">{filtered.length} analyse{filtered.length > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <i className={`text-gray-400 text-xl transition-transform duration-200 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </button>

                  {isExpanded && (
                    <DroppableCategoryArea id={droppableId} isEmpty={filtered.length === 0}>
                      {filtered.map(analyse => {
                        const isAnalyseExpanded = expandedAnalyses.has(analyse.id);
                        const msg = saveMessages[analyse.id];
                        const dirty = isAnalyseDirty(analyse.id);

                        let rangesDefinis = 0;
                        for (const rk of RANGE_KEYS) {
                          const fields = DB_FIELDS[rk.key];
                          if ((analyse as any)[fields.min] !== null || (analyse as any)[fields.max] !== null) {
                            rangesDefinis++;
                          }
                        }

                        return (
                          <div key={analyse.id} className="border-b border-gray-100 last:border-b-0">
                            <DraggableAnalyseRow id={analyse.id}>
                            {/* Ligne analyse */}
                            <button
                              onClick={() => handleExpandAnalyse(analyse.id)}
                              className="w-full flex items-center justify-between p-3 md:p-4 hover:bg-gray-50/50 transition-colors cursor-pointer text-left"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isAnalyseExpanded ? 'bg-teal-100' : 'bg-gray-100 group-hover:bg-teal-50'}`}>
                                  <i className={`ri-arrow-right-s-line text-gray-500 transition-transform duration-200 ${isAnalyseExpanded ? 'rotate-90 text-teal-600' : ''}`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    {/* Nom de l'analyse - éditable */}
                                    {editingNameId === analyse.id ? (
                                      <input
                                        type="text"
                                        value={editingNameValue}
                                        onChange={(e) => setEditingNameValue(e.target.value)}
                                        onBlur={() => handleSaveName(analyse.id, editingNameValue)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveName(analyse.id, editingNameValue);
                                          if (e.key === 'Escape') setEditingNameId(null);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="font-semibold text-gray-800 text-sm border border-teal-400 rounded-lg px-2 py-1 focus:ring-2 focus:ring-teal-500/20 outline-none min-w-[200px]"
                                        autoFocus
                                      />
                                    ) : (
                                      <span
                                        className="font-semibold text-gray-800 text-sm truncate cursor-pointer hover:text-teal-600 transition-colors border-b border-dashed border-gray-300 hover:border-teal-400"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingNameId(analyse.id);
                                          setEditingNameValue(analyse.nom);
                                        }}
                                        title="Cliquer pour renommer"
                                      >
                                        {analyse.nom}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-400 font-mono whitespace-nowrap">{analyse.code}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                    <span>{analyse.unite || 'sans unité'}</span>
                                    <span>·</span>

                                    {/* Catégorie - éditable */}
                                    {editingCategoryId === analyse.id ? (
                                      <select
                                        value={editingCategoryValue}
                                        onChange={(e) => {
                                          if (e.target.value === '__new__') {
                                            setEditingCategoryId(`${analyse.id}_new`);
                                            setEditingCategoryValue('');
                                          } else {
                                            setEditingCategoryValue(e.target.value);
                                            handleSaveCategory(analyse.id, e.target.value);
                                          }
                                        }}
                                        onBlur={() => setEditingCategoryId(null)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs border border-teal-400 rounded-lg px-2 py-1 focus:ring-2 focus:ring-teal-500/20 outline-none bg-white"
                                        autoFocus
                                      >
                                        {toutesLesCategories.map(cat => (
                                          <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                        <option value="__new__">+ Nouvelle catégorie...</option>
                                      </select>
                                    ) : editingCategoryId === `${analyse.id}_new` ? (
                                      <input
                                        type="text"
                                        value={editingCategoryValue}
                                        onChange={(e) => setEditingCategoryValue(e.target.value)}
                                        onBlur={() => {
                                          if (editingCategoryValue.trim()) {
                                            handleSaveCategory(analyse.id, editingCategoryValue);
                                          } else {
                                            setEditingCategoryId(null);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && editingCategoryValue.trim()) {
                                            handleSaveCategory(analyse.id, editingCategoryValue);
                                          }
                                          if (e.key === 'Escape') setEditingCategoryId(null);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Nouvelle catégorie..."
                                        className="text-xs border border-teal-400 rounded-lg px-2 py-1 focus:ring-2 focus:ring-teal-500/20 outline-none min-w-[150px]"
                                        autoFocus
                                      />
                                    ) : (
                                      <span
                                        className="cursor-pointer hover:text-teal-600 transition-colors border-b border-dashed border-gray-200 hover:border-teal-400 inline-flex items-center gap-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingCategoryId(analyse.id);
                                          setEditingCategoryValue(analyse.categorie);
                                        }}
                                        title="Cliquer pour changer la catégorie"
                                      >
                                        {analyse.categorie}
                                        <i className="ri-pencil-line text-[9px] text-gray-400 group-hover:text-teal-500"></i>
                                      </span>
                                    )}

                                    <span>·</span>
                                    <span>{rangesDefinis} plage{rangesDefinis > 1 ? 's' : ''} définie{rangesDefinis > 1 ? 's' : ''}</span>
                                    {analyse.description && (
                                      <>
                                        <span>·</span>
                                        <span className="truncate max-w-[200px]">{analyse.description}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                {dirty && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">
                                    Modifié
                                  </span>
                                )}
                                {msg && (
                                  <span className={`text-xs font-medium ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    {msg.type === 'success' ? (
                                      <i className="ri-check-line"></i>
                                    ) : (
                                      <i className="ri-error-warning-line"></i>
                                    )}
                                  </span>
                                )}
                              </div>
                            </button>

                            {/* Panneau d'édition */}
                            {isAnalyseExpanded && (
                              <div className="px-3 md:px-4 pb-4 pt-1 bg-gray-50/50">
                                {/* Grille des plages */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                  {RANGE_KEYS.map(rk => {
                                    const range = editions[analyse.id]?.[rk.key] || { min: '', max: '' };
                                    const fields = DB_FIELDS[rk.key];
                                    const origMin = (analyse as any)[fields.min];
                                    const origMax = (analyse as any)[fields.max];
                                    const origMinStr = origMin !== null && origMin !== undefined ? String(origMin) : '';
                                    const origMaxStr = origMax !== null && origMax !== undefined ? String(origMax) : '';
                                    const hasChange = range.min !== origMinStr || range.max !== origMaxStr;

                                    return (
                                      <div
                                        key={rk.key}
                                        className={`bg-white rounded-xl border p-3 transition-all ${hasChange ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}
                                      >
                                        <div className="flex items-center gap-1.5 mb-2">
                                          <i className={`${rk.icon} text-gray-500 text-sm`}></i>
                                          <span className="text-xs font-semibold text-gray-700">{rk.label}</span>
                                          {hasChange && (
                                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <div className="flex-1">
                                            <label className="text-[10px] text-gray-400 block mb-0.5">Min</label>
                                            <input
                                              type="text"
                                              value={range.min}
                                              onChange={(e) => handleRangeChange(analyse.id, rk.key, 'min', e.target.value)}
                                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-mono"
                                              placeholder="—"
                                            />
                                          </div>
                                          <span className="text-gray-300 text-xs pt-4">–</span>
                                          <div className="flex-1">
                                            <label className="text-[10px] text-gray-400 block mb-0.5">Max</label>
                                            <input
                                              type="text"
                                              value={range.max}
                                              onChange={(e) => handleRangeChange(analyse.id, rk.key, 'max', e.target.value)}
                                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-mono"
                                              placeholder="—"
                                            />
                                          </div>
                                        </div>
                                        {/* Valeur originale si modifiée */}
                                        {hasChange && (
                                          <p className="text-[10px] text-amber-600 mt-1">
                                            Avant : {origMinStr || '—'} – {origMaxStr || '—'}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Unité */}
                                {analyse.unite && (
                                  <p className="text-[11px] text-gray-400 mt-3 text-right">
                                    Unité : <strong>{analyse.unite}</strong>
                                  </p>
                                )}

                                {/* Bouton sauvegarder + message */}
                                <div className="flex items-center justify-end gap-3 mt-3 pt-3 border-t border-gray-200">
                                  {msg && (
                                    <span className={`text-xs font-medium ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                      {msg.type === 'success' ? (
                                        <><i className="ri-checkbox-circle-line mr-1"></i>{msg.text}</>
                                      ) : (
                                        <><i className="ri-error-warning-line mr-1"></i>{msg.text}</>
                                      )}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => {
                                      // Réinitialiser l'édition
                                      setEditions(prev => ({
                                        ...prev,
                                        [analyse.id]: initEditionForAnalyse(analyse),
                                      }));
                                      setDirtyAnalyses(prev => {
                                        const next = new Set(prev);
                                        next.delete(analyse.id);
                                        return next;
                                      });
                                    }}
                                    className="px-3.5 py-2 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-100 transition-all text-xs font-medium cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-arrow-go-back-line mr-1"></i>
                                    Réinitialiser
                                  </button>
                                  <button
                                    onClick={() => handleSaveAnalyse(analyse.id)}
                                    disabled={!isAnalyseDirty(analyse.id)}
                                    className="px-5 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold text-xs whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                                  >
                                    <i className="ri-save-line mr-1.5"></i>
                                    Enregistrer
                                  </button>
                                </div>
                              </div>
                            )}
                          </DraggableAnalyseRow>
                          </div>
                        );
                      })}
                    </DroppableCategoryArea>
                  )}
                </div>
              );
            })}
          </div>
          </DndContext>

          {catalogue.length === 0 && !loading && (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-flask-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune analyse trouvée</h3>
              <p className="text-gray-500">Le catalogue d'analyses est vide.</p>
            </div>
          )}
        </div>
      </main>

      {/* Modal de création d'une nouvelle analyse */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !creating && setShowCreateModal(false)}></div>
          <div className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* En-tête du modal */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-5 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <i className="ri-add-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Nouvelle analyse</h3>
                    <p className="text-sm text-gray-500">Créez une analyse avec ses valeurs de référence</p>
                  </div>
                </div>
                <button
                  onClick={() => !creating && setShowCreateModal(false)}
                  disabled={creating}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Corps du modal */}
            <div className="px-6 py-5 space-y-5">
              {/* Message succès */}
              {createSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  <p className="text-green-700 text-sm font-medium">{createSuccess}</p>
                </div>
              )}

              {/* Message erreur */}
              {createError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                  <p className="text-red-700 text-sm">{createError}</p>
                </div>
              )}

              {/* Section 1 : Informations de base */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-teal-600"></i>
                  Informations générales
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                      Nom de l'analyse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAnalyse.nom}
                      onChange={(e) => setNewAnalyse(prev => ({ ...prev, nom: e.target.value }))}
                      placeholder="Ex: Transaminases SGOT (ASAT)"
                      className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Code</label>
                    <input
                      type="text"
                      value={newAnalyse.code}
                      onChange={(e) => setNewAnalyse(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="Auto-généré si vide"
                      className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-mono focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Unité</label>
                    <input
                      type="text"
                      value={newAnalyse.unite}
                      onChange={(e) => setNewAnalyse(prev => ({ ...prev, unite: e.target.value }))}
                      placeholder="Ex: UI/L, g/L, mmol/L..."
                      className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                      Catégorie <span className="text-red-500">*</span>
                    </label>
                    {toutesLesCategories.length > 0 ? (
                      <>
                        <select
                          value={toutesLesCategories.includes(newAnalyse.categorie) ? newAnalyse.categorie : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setNewAnalyse(prev => ({ ...prev, categorie: '' }));
                            } else {
                              setNewAnalyse(prev => ({ ...prev, categorie: e.target.value }));
                            }
                          }}
                          className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all bg-white"
                        >
                          <option value="">-- Choisir une catégorie existante --</option>
                          {toutesLesCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                          <option value="__custom__">+ Créer une nouvelle catégorie</option>
                        </select>
                        {!toutesLesCategories.includes(newAnalyse.categorie) && (
                          <input
                            type="text"
                            value={newAnalyse.categorie}
                            onChange={(e) => setNewAnalyse(prev => ({ ...prev, categorie: e.target.value }))}
                            placeholder="Nom de la nouvelle catégorie..."
                            className="w-full mt-2 px-3.5 py-2.5 border border-teal-400 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                            autoFocus
                          />
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        value={newAnalyse.categorie}
                        onChange={(e) => setNewAnalyse(prev => ({ ...prev, categorie: e.target.value }))}
                        placeholder="Nom de la catégorie..."
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                      />
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Description (optionnelle)</label>
                  <textarea
                    value={newAnalyse.description}
                    onChange={(e) => setNewAnalyse(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brève description de l'analyse..."
                    rows={2}
                    maxLength={500}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                  />
                </div>
              </div>

              {/* Section 2 : Valeurs de référence */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-bar-chart-line text-teal-600"></i>
                  Plages de référence
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {RANGE_KEYS.map(rk => {
                    const range = newAnalyse.ranges[rk.key] || { min: '', max: '' };
                    const hasValue = range.min !== '' || range.max !== '';
                    return (
                      <div
                        key={rk.key}
                        className={`bg-white rounded-xl border p-3 transition-all ${hasValue ? 'border-teal-200 bg-teal-50/40' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          <i className={`${rk.icon} text-gray-500 text-sm`}></i>
                          <span className="text-xs font-semibold text-gray-700">{rk.label}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400 block mb-0.5">Min</label>
                            <input
                              type="text"
                              value={range.min}
                              onChange={(e) => {
                                if (e.target.value !== '' && !/^-?\d*\.?\d*$/.test(e.target.value)) return;
                                setNewAnalyse(prev => ({
                                  ...prev,
                                  ranges: {
                                    ...prev.ranges,
                                    [rk.key]: { ...prev.ranges[rk.key], min: e.target.value },
                                  },
                                }));
                              }}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-mono"
                              placeholder="—"
                            />
                          </div>
                          <span className="text-gray-300 text-xs pt-4">–</span>
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400 block mb-0.5">Max</label>
                            <input
                              type="text"
                              value={range.max}
                              onChange={(e) => {
                                if (e.target.value !== '' && !/^-?\d*\.?\d*$/.test(e.target.value)) return;
                                setNewAnalyse(prev => ({
                                  ...prev,
                                  ranges: {
                                    ...prev.ranges,
                                    [rk.key]: { ...prev.ranges[rk.key], max: e.target.value },
                                  },
                                }));
                              }}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-mono"
                              placeholder="—"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Pied du modal */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 rounded-b-2xl z-10">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-100 transition-all text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateAnalyse}
                  disabled={creating || !newAnalyse.nom.trim() || !newAnalyse.categorie.trim()}
                  className="px-6 py-2.5 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Création...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Créer l'analyse
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}